import { useState } from "react"
import { Lock } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

interface MeetingPasswordDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** May return a promise: the dialog shows "Verifying…" until it settles. */
  onSubmit: (password: string) => void | Promise<void>
  onCancel: () => void
  /** Why the last attempt failed, from the server. The dialog only knew how to
   *  say "enter a password"; whether the one entered was correct is something
   *  only the API can answer, and the joiner needs to be told which it was. */
  errorMessage?: string
}

/**
 * The meeting's own password prompt (join, rejoin, a guest at the door).
 *
 * Drawn like the one MeetingGuard shows when a meeting link is opened
 * (features/meetings/components/MeetingPasswordDialog) so both read as the same
 * question: lock header, the shared Input (with its show-password toggle), and
 * Cancel / Join. It used to carry its own header X on top of DialogContent's,
 * so it had two close buttons; DialogContent's is the one kept, and it closes
 * through `onOpenChange`, as Escape does.
 */
export function MeetingPasswordDialog({
  open,
  onOpenChange,
  onSubmit,
  onCancel,
  errorMessage,
}: MeetingPasswordDialogProps) {
  const t = useTranslation()
  const [password, setPassword] = useState("")
  const [empty, setEmpty] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!password.trim()) {
      setEmpty(true)
      return
    }
    setEmpty(false)
    setSubmitting(true)
    try {
      await onSubmit(password.trim())
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    setPassword("")
    setEmpty(false)
    onCancel()
  }

  const errorText = empty ? t("meetings.enterMeetingPassword") : errorMessage

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] sm:max-w-[420px] p-0 overflow-hidden bg-card dark:bg-card border-0 shadow-2xl"
        style={{ borderRadius: "16px" }}
        // A stray tap beside the field is not a "no": closing gives up on the
        // join, so it takes Cancel, the X or Escape.
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-4 sm:p-5 border-b border-stroke-100 dark:border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <DialogTitle className="flex-1 min-w-0 text-left text-base sm:text-lg font-semibold text-slate-800 dark:text-white">
              {t("meetings.passwordRequired")}
            </DialogTitle>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-5 space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("meetings.passwordRequiredDesc")}
          </p>

          <Input
            autoFocus
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value.slice(0, 128))
              if (empty) setEmpty(false)
            }}
            maxLength={128}
            autoComplete="current-password"
            placeholder={t("meetings.enterPassword")}
            variant="outlined"
            error={!!errorText}
            errorText={errorText}
          />

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={submitting}
              className="flex-1"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !password.trim()}
              className="flex-1 bg-primary hover:bg-primary/90 text-white"
            >
              {submitting ? t("common.verifying") : t("meetings.join")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
